import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface HelpState {
  isOpen: boolean;
  topicId: string | null;
  anchor: string | null;
}

interface HelpContextValue extends HelpState {
  openHelp: (topicId: string, anchor?: string) => void;
  closeHelp: () => void;
  setTopic: (topicId: string, anchor?: string) => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<HelpState>({ isOpen: false, topicId: null, anchor: null });

  const openHelp = useCallback((topicId: string, anchor?: string) => {
    setState({ isOpen: true, topicId, anchor: anchor ?? null });
  }, []);

  const closeHelp = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const setTopic = useCallback((topicId: string, anchor?: string) => {
    setState({ isOpen: true, topicId, anchor: anchor ?? null });
  }, []);

  const value = useMemo<HelpContextValue>(
    () => ({ ...state, openHelp, closeHelp, setTopic }),
    [state, openHelp, closeHelp, setTopic]
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp() {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within a HelpProvider');
  return ctx;
}
